import {
  Box,
  Tab,
  Tabs,
  Stack,
  Alert,
  Typography,
  Snackbar,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PetParentAnimal } from '../../../../types/PetParent';
import {
  DELETE_ALBUM_PROMPT_RESPONSE,
  GET_ALBUM_TYPEFORM,
} from '../../../../queries';
import {
  CAPTION_TYPEFORM_FIELD_ID,
  hashids,
  WELCOME_HOME_ALBUM_ID,
} from '../../../../utilities';
import CustomContainer from '../../CustomContainer';
import CustomTabPanel from '../../CustomTabPanel';
import { useHubPetParent } from '../../HubPetParentContext';
import AddPromptDialog from './AddPromptDialog';
import AlbumHeader from './AlbumHeader';
import PageHeader from './PageHeader';
import PromptCard from './PromptCard';
import { AlbumCategory } from '../useAlbumIcon';
import { track } from '../../analytics';
import useHubPageView from '../../useHubPageView';
import {
  AlbumSpecies,
  getAlbumSpecies,
  getWelcomeHomePromptsForSpecies,
  WELCOME_HOME_ALBUM_DESCRIPTION,
  WELCOME_HOME_ALBUM_TITLE,
} from '../welcomeHomeAlbum';

type PromptStatus = 'todo' | 'submitting' | 'completed' | 'error';

interface Prompt {
  id: string;
  title: string;
  description: string;
  status: PromptStatus;
  images?: string[];
  caption?: string;
  createdAt?: string;
}

interface Album {
  id: string;
  title: string;
  description: string;
  category: AlbumCategory;
  completed: number;
  total: number;
  photoCount: number;
  prompts: Prompt[];
}

interface TypeformQuestion {
  id: string;
  title: string;
  fieldType: string;
  hidden?: boolean | null;
}

interface TypeformAnswer {
  id?: string | null;
  fieldId: string;
  fieldType: string;
  text?: string | null;
}

interface TypeformResponse {
  id: string;
  submittedAt: string;
  answers: TypeformAnswer[];
}

interface AlbumTypeformData {
  albumForm?: {
    id: string;
    typeformTitle?: string | null;
    taskTitle?: string | null;
    responseTitle?: string | null;
    questions: TypeformQuestion[];
  } | null;
  responses?: TypeformResponse[];
}

const extractImageUrls = (text?: string | null): string[] => {
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.filter((value): value is string => typeof value === 'string');
    }
    if (typeof parsed === 'string') {
      return [parsed];
    }
    if (parsed && typeof parsed === 'object' && 'url' in parsed && typeof parsed.url === 'string') {
      return [parsed.url];
    }
  } catch {
    // Typeform file uploads may be stored as a plain URL instead of JSON.
  }

  return text
    .split(/[\n,]/)
    .map((url) => url.trim())
    .filter((url) => url.startsWith('http'));
};

const buildAlbum = (
  albumId: string,
  species: AlbumSpecies,
  albumForm?: AlbumTypeformData['albumForm'],
  responses: TypeformResponse[] = [],
): Album | null => {
  if (!albumForm) return null;

  const completedAnswersByFieldId = new Map<
    string,
    { caption?: string; images: string[]; submittedAt?: string }
  >();

  responses.forEach((response) => {
    const caption = response.answers?.find(
      (answer) => answer.fieldId === CAPTION_TYPEFORM_FIELD_ID,
    )?.text ?? undefined;

    response.answers?.forEach((answer) => {
      if (answer.id && answer.fieldType === 'file_upload') {
        const existingAnswer = completedAnswersByFieldId.get(answer.fieldId);
        const images = extractImageUrls(answer.text);
        completedAnswersByFieldId.set(answer.fieldId, {
          caption: caption ?? existingAnswer?.caption,
          images: [...(existingAnswer?.images ?? []), ...images],
          submittedAt: response.submittedAt,
        });
      }
    });
  });

  const promptTemplates = getWelcomeHomePromptsForSpecies(species);
  const prompts = albumForm.questions
    .filter((question) => !question.hidden && question.id !== CAPTION_TYPEFORM_FIELD_ID)
    .slice(0, promptTemplates.length)
    .map((question, index) => {
      const completedAnswer = completedAnswersByFieldId.get(question.id);
      const promptTemplate = promptTemplates[index];
      return {
        id: question.id,
        title: promptTemplate?.title || question.title,
        description: promptTemplate?.description || '',
        status: completedAnswer ? 'completed' as const : 'todo' as const,
        images: completedAnswer?.images,
        caption: completedAnswer?.caption,
        createdAt: completedAnswer?.submittedAt,
      };
    });

  const completed = prompts.filter((prompt) => prompt.status === 'completed').length;
  const photoCount = prompts.reduce((total, prompt) => total + (prompt.images?.length ?? 0), 0);

  return {
    id: albumId,
    title: WELCOME_HOME_ALBUM_TITLE,
    description: WELCOME_HOME_ALBUM_DESCRIPTION,
    category: 'welcome-home',
    completed,
    total: prompts.length,
    photoCount,
    prompts,
  };
};

function a11yProps(name: string) {
  return {
    id: `album-tab-${name}`,
    'aria-controls': `album-tabpanel-${name}`,
  };
}

const buildAlbumDetailLogPayload = (
  context: Record<string, unknown>,
  prompts?: Prompt[],
) => ({
  ...context,
  allPromptIds: prompts?.map((prompt) => prompt.id) ?? [],
  prompts: prompts?.map((prompt) => ({
    id: prompt.id,
    title: prompt.title,
    status: prompt.status,
    imageCount: prompt.images?.length ?? 0,
  })) ?? [],
});

const logAlbumDetail = (
  message: string,
  context: Record<string, unknown>,
  prompts?: Prompt[],
) => {
  // eslint-disable-next-line no-console
  console.log('[AlbumDetail]', message, buildAlbumDetailLogPayload(context, prompts));
};

const logAlbumDetailError = (
  message: string,
  context: Record<string, unknown>,
  prompts?: Prompt[],
) => {
  // eslint-disable-next-line no-console
  console.error('[AlbumDetail]', message, buildAlbumDetailLogPayload(context, prompts));
};

function AlbumDetail() {
  const [activeTab, setActiveTab] = useState(0);
  const [activePromptId, setActivePromptId] = useState<string | null>(null);
  const [submittingPromptIds, setSubmittingPromptIds] = useState<string[]>([]);
  const [errorPromptIds, setErrorPromptIds] = useState<string[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const { albumId, petId } = useParams<{ albumId: string; petId: string }>();
  const { petParent, loading } = useHubPetParent();

  const pets = petParent?.animals || [];
  const decodedPetId = petId ? hashids.decode(petId)[0]?.toString() : null;
  const currentPet = pets.find((p: PetParentAnimal) => p.id === decodedPetId) || pets[0];
  const currentSpecies: AlbumSpecies = getAlbumSpecies(currentPet);
  const {
    data: albumTypeformData,
    loading: albumLoading,
    error: albumError,
    refetch: refetchAlbum,
  } = useQuery<
    AlbumTypeformData,
    { typeformFormId: string; ppId: string; animalId: string }
  >(GET_ALBUM_TYPEFORM, {
    variables: {
      typeformFormId: WELCOME_HOME_ALBUM_ID || '',
      ppId: petParent?.id || '',
      animalId: decodedPetId || '',
    },
    skip: !petParent?.id || !decodedPetId || !WELCOME_HOME_ALBUM_ID,
  });
  const [deleteAlbumPromptResponse] = useMutation<
    { deleteAlbumPromptResponse: boolean },
    { animalId: string; typeformFormId: string; fieldId: string }
  >(DELETE_ALBUM_PROMPT_RESPONSE);
  const album = albumId
    ? buildAlbum(
      albumId,
      currentSpecies,
      albumTypeformData?.albumForm,
      albumTypeformData?.responses,
    )
    : null;

  // Album props arrive async (typeform query); gate the page_view until the album resolves
  // so we don't emit a property-less page_view during loading.
  useHubPageView(
    { album_name: album?.title, species: currentSpecies },
    [album?.title, currentSpecies],
    Boolean(album),
  );

  useEffect(() => {
    if (albumError) {
      logAlbumDetailError('album query failed', {
        albumError: albumError.message,
        graphQLErrors: albumError.graphQLErrors,
        networkError: albumError.networkError,
        typeformFormId: WELCOME_HOME_ALBUM_ID,
        animalId: decodedPetId,
        petParentId: petParent?.id,
      }, album?.prompts);
    }
  }, [albumError, album, decodedPetId, petParent?.id]);

  useEffect(() => {
    if (errorPromptIds.length === 0) return;
    logAlbumDetailError('prompts in error state', {
      errorPromptIds,
      submittingPromptIds,
      activePromptId,
    }, album?.prompts);
  }, [errorPromptIds, submittingPromptIds, activePromptId, album]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddPrompt = (promptId: string) => {
    track('album_prompt_add_click', {
      prompt_name: album?.prompts.find((p) => p.id === promptId)?.title,
      album_name: album?.title,
      species: currentSpecies,
    });
    setErrorPromptIds((prev) => prev.filter((id) => id !== promptId));
    setActivePromptId(promptId);
  };

  const handleDialogClose = () => {
    setActivePromptId(null);
  };

  const handlePromptSubmitted = () => {
    const submittedPromptId = activePromptId;
    if (!albumId || !submittedPromptId) return;
    const submittedPromptIndex = album?.prompts.findIndex(
      (prompt) => prompt.id === submittedPromptId,
    ) ?? -1;
    const submittedPromptNumber = submittedPromptIndex >= 0 ? submittedPromptIndex + 1 : 0;
    logAlbumDetail('prompt submitted', {
      submittedPromptId,
      submittedPromptNumber,
      submittedPromptTitle: album?.prompts[submittedPromptIndex]?.title,
      typeformFormId: WELCOME_HOME_ALBUM_ID,
      animalId: currentPet?.id,
      petParentId: petParent?.id,
      albumId,
    }, album?.prompts);
    setSubmittingPromptIds((prev) => (
      prev.includes(submittedPromptId) ? prev : [...prev, submittedPromptId]
    ));
    setSnackbarMessage('Memory submitted');
    setTimeout(() => {
      refetchAlbum()
        .then((result) => {
          const refreshedAlbum = buildAlbum(
            albumId,
            currentSpecies,
            result.data?.albumForm,
            result.data?.responses,
          );
          const refreshedPrompt = refreshedAlbum?.prompts.find(
            (p) => p.id === submittedPromptId,
          );
          const hasSubmittedPhoto = refreshedPrompt?.status === 'completed'
            && (refreshedPrompt.images?.length ?? 0) > 0;
          if (hasSubmittedPhoto) {
            setErrorPromptIds((prev) => prev.filter((id) => id !== submittedPromptId));
          } else {
            logAlbumDetailError('submitted photo not found after refetch', {
              submittedPromptId,
              refreshedPrompt: refreshedPrompt ?? null,
              typeformFormId: WELCOME_HOME_ALBUM_ID,
              animalId: currentPet?.id,
            }, refreshedAlbum?.prompts ?? album?.prompts);
            setErrorPromptIds((prev) => (
              prev.includes(submittedPromptId) ? prev : [...prev, submittedPromptId]
            ));
          }
        })
        .catch((refetchError) => {
          logAlbumDetailError('refetch failed after submit', {
            submittedPromptId,
            error: refetchError,
            typeformFormId: WELCOME_HOME_ALBUM_ID,
            animalId: currentPet?.id,
          }, album?.prompts);
          setErrorPromptIds((prev) => (
            prev.includes(submittedPromptId) ? prev : [...prev, submittedPromptId]
          ));
        })
        .finally(() => {
          setSubmittingPromptIds((prev) => prev.filter((id) => id !== submittedPromptId));
        });
    }, 3000);
  };

  const handleDeletePrompt = async (promptId: string) => {
    if (!promptId || !currentPet?.id || !WELCOME_HOME_ALBUM_ID) return;
    setErrorPromptIds((prev) => prev.filter((id) => id !== promptId));
    try {
      const { data } = await deleteAlbumPromptResponse({
        variables: {
          animalId: currentPet.id,
          typeformFormId: WELCOME_HOME_ALBUM_ID,
          fieldId: promptId,
        },
      });
      if (!data?.deleteAlbumPromptResponse) {
        logAlbumDetailError('delete mutation returned false', {
          promptId,
          typeformFormId: WELCOME_HOME_ALBUM_ID,
          animalId: currentPet.id,
          petParentId: petParent?.id,
        }, album?.prompts);
        return;
      }
      await refetchAlbum();
      setSnackbarMessage('Memory deleted');
    } catch (deleteError) {
      logAlbumDetailError('delete failed', {
        promptId,
        error: deleteError,
        typeformFormId: WELCOME_HOME_ALBUM_ID,
        animalId: currentPet.id,
        petParentId: petParent?.id,
      }, album?.prompts);
    }
  };

  const handlePageReload = () => {
    window.location.reload();
  };

  if (loading || albumLoading) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Typography>Loading...</Typography>
      </CustomContainer>
    );
  }

  if (!WELCOME_HOME_ALBUM_ID) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Alert severity="error">Welcome Home album is not configured</Alert>
      </CustomContainer>
    );
  }

  if (albumError) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Alert severity="error">Unable to load album</Alert>
      </CustomContainer>
    );
  }

  if (!album || !currentPet) {
    return (
      <CustomContainer sx={{ pt: 4 }}>
        <Alert severity="error">Album not found</Alert>
      </CustomContainer>
    );
  }

  const allPrompts = album.prompts;
  const todoPrompts = allPrompts.filter(
    (p: Prompt) => p.status !== 'completed',
  );
  const completedPrompts = allPrompts.filter(
    (p: Prompt) => p.status === 'completed',
  );

  const showAllPromptsTab = todoPrompts.length > 0 && completedPrompts.length > 0;
  const showToDoTab = todoPrompts.length > 0 && todoPrompts.length !== allPrompts.length;
  const showCompleteTab = completedPrompts.length > 0
    && completedPrompts.length !== allPrompts.length;
  const tabsShown = showAllPromptsTab || showToDoTab || showCompleteTab;

  const getPromptsForTab = (tabIndex: number): Prompt[] => {
    switch (tabIndex) {
      case 0:
        return allPrompts;
      case 1:
        return todoPrompts;
      case 2:
        return completedPrompts;
      default:
        return allPrompts;
    }
  };

  const getPromptDisplayStatus = (prompt: Prompt): PromptStatus => {
    if (submittingPromptIds.includes(prompt.id)) return 'submitting';
    if (errorPromptIds.includes(prompt.id)) return 'error';
    return prompt.status;
  };

  const currentPrompts = getPromptsForTab(activeTab).map((prompt) => ({
    ...prompt,
    status: getPromptDisplayStatus(prompt),
  }));

  const fillPetName = (text: string) => text
      .replace(/\[pet name\]/g, currentPet.name)
      .replace(/Your Pet/g, currentPet.name)
      .replace(/your pet/g, currentPet.name);

  const activePromptIndex = activePromptId
    ? allPrompts.findIndex((p: Prompt) => p.id === activePromptId)
    : -1;
  const activePrompt = activePromptIndex >= 0 ? allPrompts[activePromptIndex] : null;
  // Typeform `prompt_number` is 1-indexed (e.g. "First Nap" is question #3).
  const activePromptNumber = activePromptIndex >= 0 ? activePromptIndex + 1 : 0;

  return (
    <Box sx={{ minHeight: '100vh', mt: 3 }}>
      <PageHeader
        title={album.title}
        description={fillPetName(album.description)}
        category={album.category}
      />

      <CustomContainer wide>
        <AlbumHeader
          petName={currentPet.name}
          petType={currentPet.animalType.toLowerCase() as 'dog' | 'cat'}
          completed={album.completed}
          total={album.total}
          photoCount={album.photoCount}
          albumId={album.id}
          albumName={album.title}
          species={currentSpecies}
          petId={hashids.encode(currentPet.id)}
          onCopySuccess={() => setSnackbarMessage('Link copied')}
        />
      </CustomContainer>

      {tabsShown && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <CustomContainer wide>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="album prompts tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'uppercase',
                  fontSize: '14px',
                  fontWeight: 500,
                  letterSpacing: '0.4px',
                },
              }}
            >
              {showAllPromptsTab && (
                <Tab label="All Prompts" {...a11yProps('all')} />
              )}
              {showToDoTab && <Tab label="To Do" {...a11yProps('todo')} />}
              {showCompleteTab && (
                <Tab label="Completed" {...a11yProps('completed')} />
              )}
            </Tabs>
          </CustomContainer>
        </Box>
      )}

      <Box sx={{ backgroundColor: '#F5F5F5' }}>
        <CustomContainer sx={{ py: 5 }}>
          <CustomTabPanel label={`album-${activeTab}`}>
            <Stack spacing={3}>
              {currentPrompts.map((prompt: Prompt) => (
                <PromptCard
                  key={prompt.id}
                  title={fillPetName(prompt.title)}
                  description={fillPetName(prompt.description)}
                  status={prompt.status}
                  images={prompt.images}
                  caption={prompt.caption}
                  createdAt={prompt.createdAt}
                  onAdd={() => handleAddPrompt(prompt.id)}
                  onDelete={() => handleDeletePrompt(prompt.id)}
                  onReload={() => handlePageReload()}
                />
              ))}
            </Stack>
          </CustomTabPanel>
        </CustomContainer>
      </Box>

      {activePrompt && (
        <AddPromptDialog
          open={Boolean(activePromptId)}
          onClose={handleDialogClose}
          onSubmitted={handlePromptSubmitted}
          petName={currentPet.name}
          petParentEmail={petParent?.email || ''}
          animalId={currentPet.id}
          promptNumber={activePromptNumber}
        />
      )}

      <Snackbar
        open={snackbarMessage !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbarMessage}
      />
    </Box>
  );
}

export default AlbumDetail;
